import * as coveo from "@coveo/headless"
import * as productRecommendation from "@coveo/headless/product-recommendation"
import * as recommendation from "@coveo/headless/recommendation"
import EventTarget from "@ungap/event-target"
import { useConsole } from "contexts/Console"
import { createContext, useContext, useRef } from "react"
import exposeEventTarget from "utils/exposeEventTarget"

const isPageLoaded = () => {
  return typeof document !== "undefined" && document.readyState === "complete"
}

const ensurePageLoaded = async () => {
  if (isPageLoaded()) return
  await new Promise(resolve => {
    window.addEventListener("load", resolve, { once: true })
  })
}

const Context = createContext({})

const useCoveo = () => {
  return useContext(Context)
}

const optionsForSearchBox = {
  options: {
    highlightOptions: {
      exactMatchDelimiters: {
        open: "<strong>",
        close: "</strong>",
      },
      correctionDelimiters: {
        open: "<i>",
        close: "</i>",
      },
    },
  },
}

const coveo2RlxMapping = {
  "rlxcom-grids": "pipelineGrids",
  "rlxcom-web-search": "pipelineWebsearch",
  "rlxcom-web-recommendation": "pipelineWebRecommendation",
  rlxcom_testimonees: "pipelineTestimonees",
  rlxcom_testimonees_recommendation: "pipelineTestimoneesRecommendation",
  "rlxcom-plp": "pipelineCoveoPLP",
}

const rlx2CoveoMapping = {
  pipelineGrids: "rlxcom-grids",
  pipelineWebsearch: "rlxcom-web-search",
  pipelineWebRecommendation: "rlxcom-web-recommendation",
  pipelineTestimonees: "rlxcom_testimonees",
  pipelineTestimoneesRecommendation: "rlxcom_testimonees_recommendation",
  pipelineCoveoPLP: "rlxcom-plp",
}

const resultResponse = [] //TODO ???

const decodeJWT = token => {
  if (typeof token !== "string") return null
  const parts = token.split(".")
  if (parts.length !== 3) return null
  try {
    const payload = parts[1]
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/")
    let jsonStr
    if (typeof Buffer !== "undefined") {
      jsonStr = Buffer.from(base64, "base64").toString("utf8")
    } else if (typeof atob !== "undefined") {
      const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=")
      jsonStr = atob(padded)
    } else {
      return null
    }
    return JSON.parse(jsonStr)
  } catch (e) {
    return null
  }
}

const getExpiresFromTokens = tokensObj => {
  if (!tokensObj) return null
  let minExpires = null
  for (const token of Object.values(tokensObj)) {
    if (typeof token === "string" && token) {
      const decoded = decodeJWT(token)
      if (decoded && typeof decoded.exp === "number") {
        const expiresTime = decoded.exp * 1000
        if (minExpires === null || expiresTime < minExpires) {
          minExpires = expiresTime
        }
      }
    }
  }
  return minExpires
}

const mapTokensArrayToObject = tokensArray => {
  if (!tokensArray) return null
  if (!Array.isArray(tokensArray)) return tokensArray
  return {
    [rlx2CoveoMapping.pipelineGrids]: tokensArray[0] || null,
    [rlx2CoveoMapping.pipelineWebsearch]: tokensArray[1] || null,
    [rlx2CoveoMapping.pipelineWebRecommendation]: tokensArray[2] || null,
    [rlx2CoveoMapping.pipelineTestimonees]: tokensArray[3] || null,
    [rlx2CoveoMapping.pipelineTestimoneesRecommendation]: tokensArray[4] || null,
    [rlx2CoveoMapping.pipelineCoveoPLP]: tokensArray[5] || null,
  }
}

let _refresh
const Coveo = ({ children, initialState, tokens = null }) => {
  const { env } = initialState
  const localeCode = initialState?.current_locale?.codes?.ws || "en_US"
  const console = useConsole()

  const etRef = useRef(null)
  if (process.browser && !etRef.current) {
    etRef.current = new EventTarget()
  }

  const getReferrer = () => {
    return env?.hostname ?? env?.statics?.includes("rolex.cn") ? "rolex.cn" : "rolex.com"
  }

  const refresh = async () => {
    if (process.browser && !isPageLoaded()) {
      await ensurePageLoaded()
    }
    const url = new URL(`/api/catalog/${env.searchTokenRoute}/${env.searchTokenEndpoint}`, env?.services || global.location.origin)

    let body
    while (!body) {
      const headers = {
        "Content-Type": "application/json",
        "x-timestamp": Date.now(),
      }

      if (global.correlationId) {
        headers["x-correlation-id"] = global.correlationId
      }

      const request = new Request(url, {
        method: "GET",
        credentials: env?.withCredentials ? "include" : "same-origin",
        headers,
      })

      try {
        const response = await fetch(request)
        const correlationId = response.headers.get("x-correlation-id")
        if (correlationId && !global.correlationId) {
          global.correlationId = correlationId
          localStorage.setItem("correlationId", correlationId)
        }

        if (response.status === 503) {
          const retryAfter = Number.parseInt(response.headers.get("Retry-After") || "5", 10)
          console.warn(`Coveo:refresh(Service temporarily unavailable (503), retrying after ${retryAfter} seconds)`)

          await new Promise(resolve => setTimeout(resolve, retryAfter * 1000))
          continue
        }

        if (response.status === 502) {
          throw new Error("Token service unavailable")
        }

        if (response.status !== 200) {
          throw new Error(`Unexpected response status: ${response.status}`)
        }

        if (!response.ok) {
          throw new Error("fetch failed")
        }

        body = await response.json()
        const tokenFailed = response.headers.get("x-token-failed")
        if (tokenFailed) {
          console.warn("Failed to get tokens for keys:", tokenFailed)
        }

        if (!body) {
          throw new Error("No body")
        }

        if (body) {
          localStorage.setItem("__coveo.tokens.list", JSON.stringify(body))
          localStorage.setItem("__coveo.tokens.expires", Date.now() + 1000 * 60 * 15)
          if (etRef.current) {
            etRef.current.dispatchEvent(new Event("refresh"))
          }
        }
      } catch (err) {
        console.error("Coveo:refresh failed:", err)
        await new Promise(resolve => setTimeout(resolve, 5000))
      }
    }

    console.verbose("Coveo:refresh(%o)", body)
  }

  const ctx = {
    ...(etRef.current ? exposeEventTarget(etRef.current) : {}),
    __expires: null,
    get expires() {
      if (ctx.__expires === null) {
        const tokenExpires = getExpiresFromTokens(ctx.tokens)
        if (tokenExpires) {
          ctx.__expires = tokenExpires
        } else {
          ctx.__expires = +localStorage?.getItem?.("__coveo.tokens.expires") || 0
        }
      }
      return ctx.__expires
    },
    __tokens: null,
    get tokens() {
      try {
        if (!ctx.__tokens) {
          ctx.__tokens = mapTokensArrayToObject(tokens) ?? JSON.parse(localStorage?.getItem?.("__coveo.tokens.list") ?? "{}")
        }
        return ctx.__tokens
      } catch {
        return {}
      }
    },

    get pipelineGrids() {
      return ctx.tokens?.[rlx2CoveoMapping.pipelineGrids]
    },
    get pipelineWebsearch() {
      return ctx.tokens?.[rlx2CoveoMapping.pipelineWebsearch]
    },
    get pipelineWebRecommendation() {
      return ctx.tokens?.[rlx2CoveoMapping.pipelineWebRecommendation]
    },
    get pipelineTestimonees() {
      return ctx.tokens?.[rlx2CoveoMapping.pipelineTestimonees]
    },
    get pipelineTestimoneesRecommendation() {
      return ctx.tokens?.[rlx2CoveoMapping.pipelineTestimoneesRecommendation]
    },
    get pipelineCoveoPLP() {
      return ctx.tokens?.[rlx2CoveoMapping.pipelineCoveoPLP]
    },

    check: () => {
      const ok = ctx.tokens && Object.keys(coveo2RlxMapping).every(key => Object.hasOwn(ctx.tokens, key)) && ctx.expires - Date.now() > 1000 * 60 * 5

      console.verbose("Coveo:check(%o) => %o (%os)", { tokens: ctx.tokens, expires: ctx.expires }, ok, (ctx.expires - Date.now()) / 1000)
      if (ok) return true

      // if not ok, cleanup
      // TODO: how to destroy coveo objects?
      ctx.__expires = null
      ctx.__tokens = null
      ctx.__engine = null
      ctx.__searchBox = null
      ctx.__recommendationEngine = null
      ctx.__testimoneesRecommendationEngine = null
      return false
    },
    __engine: null,
    engine: async () => {
      console.verbose("Coveo:engine()")
      await ctx.sync()
      if (ctx.__engine) return ctx.__engine

      if (!ctx.check()) {
        throw new Error("Coveo:engine() - no valid tokens")
      }

      ctx.__engine = coveo.buildSearchEngine({
        configuration: {
          organizationId: env.organizationId,
          accessToken: ctx.pipelineWebsearch,
          organizationEndpoints: coveo.getOrganizationEndpoints(env.organizationId),
          search: {
            searchHub: env.pipeline,
            preprocessSearchResponseMiddleware: response => {
              resultResponse.push(...response.body.results)
              return response
            },
          },
          preprocessRequest: (request, clientOrigin) => {
            const referrer = getReferrer()

            if (clientOrigin === "analyticsFetch") {
              const body = request.body
              const jsonBody = JSON.parse(body)
              const actionCause = jsonBody.actionCause
              if (actionCause === "documentOpen") {
                const documentId = jsonBody.customData.contentIDValue

                let customData = {
                  ...jsonBody.customData,
                  product_brand: "rolex",
                  siteName: "rolex.com",
                }

                const matchedResult = resultResponse.find(result => documentId === result.raw.permanentid)

                if (matchedResult) {
                  if (matchedResult.raw.rlx_family_facet) {
                    customData = {
                      ...customData,
                      product_variant: matchedResult.raw.rlx_family_facet[0],
                      product_name: matchedResult.title,
                      product_price: null,
                      product_category: matchedResult.raw.rlx_familycode,
                    }
                  }
                }
                const newJsonBody = {
                  ...jsonBody,
                  referrer,
                  customData,
                }
                const newRequest = {
                  ...request,
                  body: JSON.stringify(newJsonBody),
                }
                return newRequest
              }
            }

            const body = request.body
            const jsonBody = JSON.parse(body)
            const newJsonBody = {
              ...jsonBody,
              originLevel2: (() => {
                // this is a temporary solution to get the correct originLevel2, we need more info from VTC & Coveo
                if (jsonBody.sourceName?.includes("rlx_catalog")) {
                  return "watches"
                }
                if (jsonBody.sourceName?.includes("articles")) {
                  return "articles"
                }
                return jsonBody.originLevel2
              })(),
              referrer,
            }
            const newRequest = {
              ...request,
              body: JSON.stringify(newJsonBody),
            }
            return newRequest
          },
        },
      })

      const fieldsToInclude = [
        "rlx_contenttype",
        "rlx_rmc",
        "rlx_title",
        "rlx_family",
        "rlx_family_facet",
        "rlx_case_title",
        "rlx_newmodelselection",
        "rlx_familycode",
        "rlx_description",
        "rlx_searchimage",
        "rlx_search_section",
        "rlx_search_title",
        "rlx_path",
      ]
      const { registerFieldsToInclude } = coveo.loadFieldActions(ctx.__engine)
      ctx.__engine.dispatch(registerFieldsToInclude(fieldsToInclude))

      // Behavior moved from SearchWrapper
      const { logInterfaceLoad } = coveo.loadSearchAnalyticsActions(ctx.__engine)
      const { executeSearch } = coveo.loadSearchActions(ctx.__engine)
      // ctx.__engine.dispatch(executeSearch(logInterfaceLoad()))

      // Set results per page to 1000
      coveo.buildResultsPerPage(ctx.__engine, {
        initialState: { numberOfResults: 1000 },
      })

      return ctx.__engine
    },
    __searchBox: null,
    searchBox: async (callback = () => {}) => {
      console.verbose("Coveo:searchBox()")
      let error
      await ctx.sync().catch(err => {
        error = err
        callback(err, null)
      })

      if (error) {
        return
      }

      if (ctx.__searchBox) {
        return callback(null, ctx.__searchBox)
      }

      const engine = await ctx.engine().catch(err => {
        error = err
        callback(err, null)
      })

      if (error) {
        return
      }

      try {
        ctx.__searchBox = coveo.buildSearchBox(engine, optionsForSearchBox)

        callback(null, ctx.__searchBox)
      } catch (err) {
        error = err
        callback(err, null)
      }
    },
    sync: async () => {
      if (ctx.check()) {
        console.log("Coveo:sync() - already synced")
        return
      }
      console.log("Coveo:sync() - syncing")
      _refresh ??= refresh().then(() => {
        _refresh = null
      })
      return _refresh
    },
    __recommendationEngine: null,
    recommendationEngine: async () => {
      console.verbose("Coveo:recommendationEngine()")
      await ctx.sync()
      if (ctx.__recommendationEngine) return ctx.__recommendationEngine

      if (!ctx.check()) {
        throw new Error("Coveo:recommendationEngine() - no valid tokens")
      }

      ctx.__recommendationEngine = productRecommendation.buildProductRecommendationEngine({
        configuration: {
          organizationId: env.organizationId,
          accessToken: ctx.pipelineWebRecommendation,
          organizationEndpoints: coveo.getOrganizationEndpoints(env.organizationId),
          pipeline: env.pipelineWebRecommendation,
          searchHub: env.pipelineWebRecommendation,
          search: {
            searchHub: env.pipelineWebRecommendation,
          },
          locale: localeCode,
        },
      })

      return ctx.__recommendationEngine
    },
    __testimoneesRecommendationEngine: null,
    testimoneesRecommendationEngine: async () => {
      console.verbose("Coveo:testimoneesRecommendationEngine()")
      await ctx.sync()
      if (ctx.__testimoneesRecommendationEngine) return ctx.__testimoneesRecommendationEngine

      if (!ctx.check()) {
        throw new Error("Coveo:testimoneesRecommendationEngine() - no valid tokens")
      }

      ctx.__testimoneesRecommendationEngine = recommendation.buildRecommendationEngine({
        configuration: {
          organizationId: env.organizationId,
          accessToken: ctx.pipelineTestimoneesRecommendation,
          organizationEndpoints: coveo.getOrganizationEndpoints(env.organizationId),
          pipeline: env.pipelineTestimoneesRecommendation,
          searchHub: env.pipelineTestimoneesRecommendation,
          search: {
            searchHub: env.pipelineTestimoneesRecommendation,
          },
          locale: localeCode,
        },
      })

      return ctx.__testimoneesRecommendationEngine
    },
  }

  console.verbose("Coveo(%o)", {})
  return <Context.Provider value={ctx}>{children}</Context.Provider>
}

export default Coveo
export { useCoveo }
